import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { setShowMessage } from "redux/slices/uiSlice";

const baseUrl = "/roles";
const initialState = {
  roles: [],
  role: {},
  loading: false,
};

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    setRole: (state, action) => {
      state.role = action.payload;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    setLoading: (state) => {
      state.loading = true;
    },
    setReady: (state) => {
      state.loading = false;
    },
    clearRoles: (state) => {
      state.roles = [];
      state.role = null;
    },
  },
});

export const { setRole, setRoles, setLoading, setReady, clearRoles } = rolesSlice.actions;
export default rolesSlice.reducer;

// get all roles
export const getAllRoles = () => async (dispatch) => {
  dispatch(setLoading());

  try {
    const roles = await coreApi.fetch(baseUrl, "GET");
    dispatch(setRoles(roles));
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: "Failed to fetch roles",
        type: "error",
      })
    );
  } finally {
    dispatch(setReady());
  }
};

// get role by Id
export const getRoleById = (id) => async (dispatch) => {
  dispatch(setLoading());

  try {
    const url = baseUrl + `/${id}`;
    const role = await coreApi.fetch(url, "GET");
    dispatch(setRole(role));
  } catch (err) {
    console.error(err);
    dispatch(
      setShowMessage({
        description: "Failed to fetch role",
        type: "error",
      })
    );
  } finally {
    dispatch(setReady());
  }
};

// add role
export const addRole = (payload) => async (dispatch) => {
  dispatch(setLoading());

  try {
    const res = await coreApi.post(baseUrl, payload);
    dispatch(
      setShowMessage({
        description: "Role created successfully",
        type: "success",
      })
    );
    return res;
  } catch (err) {
    if (err.response?.status === 409) {
      dispatch(
        setShowMessage({
          description: "Role already exists",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed to create role",
          type: "error",
        })
      );
    }
  } finally {
    dispatch(setReady());
  }
};

// Update a role
export const updateRole = (id, payload) => async (dispatch) => {
  dispatch(setLoading());

  try {
    const url = baseUrl + `/${id}`;
    const res = await coreApi.put(url, payload);
    dispatch(
      setShowMessage({
        description: "Role updated successfully",
        type: "success",
      })
    );
    return res;
  } catch (err) {
    if (err.response?.status === 409) {
      dispatch(
        setShowMessage({
          description: "Role already exists",
          type: "error",
        })
      );
    } else {
      dispatch(
        setShowMessage({
          description: "Failed to update role",
          type: "error",
        })
      );
    }
  } finally {
    dispatch(setReady());
  }
};

// Selectors
const rolesSelector = ({ roles }) => roles?.roles || [];
const roleSelector = ({ roles }) => roles?.role || {};
const rolesStatusSelector = ({ roles }) => roles.loading;

export const selectRoles = createSelector(rolesSelector, (roles) => roles);
export const selectRole = createSelector(roleSelector, (role) => role);
export const selectRolesStatus = createSelector(rolesStatusSelector, (loading) => loading);
